"use client"

import dynamic from "next/dynamic"
import { Text, clx } from "@medusajs/ui"
import {
  getGlsDeliveryPointTypeLabel,
} from "@/lib/util/gls-shipping"
import type { GlsPointTypeFilter } from "@/lib/util/gls-shipping"
import { HttpTypes } from "@medusajs/types"
import Button from "@/modules/common/components/button"
import GlsDeliveryPointList from "./gls-delivery-point-list"
import { useGlsDeliveryPoints } from "./use-gls-delivery-points"

const GlsDeliveryPointMap = dynamic(() => import("./gls-delivery-point-map"), {
  ssr: false,
  loading: () => (
    <div className="h-72 w-full rounded-md border border-ui-border-base bg-ui-bg-base animate-pulse" />
  ),
})

type GlsDeliveryPointPickerProps = {
  cart: HttpTypes.StoreCart
  countryCode?: string
  onSaved?: () => void | Promise<void>
}

const TYPE_FILTERS: Array<{ label: string; value: GlsPointTypeFilter }> = [
  { label: "Vse", value: null },
  { label: "Paketomat", value: 2 },
  { label: "Prevzemno mesto", value: 1 },
]

const GlsDeliveryPointPicker = ({
  cart,
  countryCode = "SI",
  onSaved,
}: GlsDeliveryPointPickerProps) => {
  const {
    query,
    setQuery,
    typeFilter,
    setTypeFilter,
    homeCoordinate,
    selectedPoint,
    focusedMatchcode,
    loading,
    saving,
    error,
    postalCode,
    isSearchingAll,
    isBrowsing,
    mapPoints,
    mapAutoFitKey,
    visiblePoints,
    selectPoint,
    focusPoint,
    startBrowsing,
    contextMessage,
  } = useGlsDeliveryPoints({ cart, countryCode, onSaved })

  const showResults = Boolean(postalCode || isSearchingAll)
  const showConfirmation = Boolean(selectedPoint) && !isBrowsing

  if (showConfirmation && selectedPoint) {
    return (
      <div className="mt-4 rounded-lg border border-ui-border-base p-4 bg-ui-bg-subtle">
        <div className="rounded-lg border-2 border-green-600 bg-green-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-600 text-white text-lg">
              ✓
            </div>
            <div className="flex-1 min-w-0">
              <Text className="txt-medium-plus text-green-900">
                Prevzemno mesto izbrano
              </Text>
              <Text className="txt-small-plus mt-1 text-neutral-900">
                {selectedPoint.name}
              </Text>
              <Text className="txt-small text-neutral-600 mt-1">
                {[selectedPoint.street, selectedPoint.houseNumber]
                  .filter(Boolean)
                  .join(" ")}
                , {selectedPoint.zipCode} {selectedPoint.city}
              </Text>
              <Text className="txt-compact-small text-neutral-500 mt-2">
                {getGlsDeliveryPointTypeLabel(selectedPoint.type)}
              </Text>
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="secondary"
          className="w-full mt-4"
          onClick={startBrowsing}
        >
          Spremeni prevzemno mesto
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-4 rounded-lg border border-ui-border-base p-4 bg-ui-bg-subtle">
      <Text className="txt-medium-plus mb-1">
        Izberite GLS prevzemno mesto
      </Text>
      <Text className="txt-small text-ui-fg-subtle mb-4">
        {contextMessage}
      </Text>

      {!loading && !error && (
        <>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Išči po kraju, poštni številki ali ulici..."
            className="w-full rounded-md border border-ui-border-base px-3 py-2 txt-small mb-3"
          />

          <div className="flex flex-wrap gap-2 mb-4">
            {TYPE_FILTERS.map((filter) => {
              const isActive = typeFilter === filter.value

              return (
                <button
                  key={filter.label}
                  type="button"
                  onClick={() => setTypeFilter(filter.value)}
                  className={clx(
                    "rounded-full px-3 py-1 txt-compact-small border transition-colors",
                    isActive
                      ? "border-neutral-900 bg-neutral-900 text-white"
                      : "border-ui-border-base bg-ui-bg-base text-ui-fg-subtle hover:border-ui-border-strong"
                  )}
                >
                  {filter.label}
                </button>
              )
            })}
          </div>
        </>
      )}

      {loading ? (
        <Text className="txt-small text-ui-fg-subtle py-4">
          Nalagam prevzemna mesta...
        </Text>
      ) : error ? (
        <Text className="txt-small text-ui-fg-error py-2">{error}</Text>
      ) : !showResults ? (
        <Text className="txt-small text-ui-fg-subtle py-2">
          Ko vnesete naslov, bomo tukaj prikazali ustrezna prevzemna mesta.
        </Text>
      ) : (
        <>
          <div className="mb-4">
            <GlsDeliveryPointMap
              points={mapPoints}
              homeCoordinate={homeCoordinate}
              selectedMatchcode={selectedPoint?.matchcode}
              focusedMatchcode={focusedMatchcode}
              autoFitKey={mapAutoFitKey}
              onSelectPoint={selectPoint}
              onFocusPoint={focusPoint}
            />
          </div>

          {visiblePoints.length === 0 ? (
            <Text className="txt-small text-ui-fg-subtle py-2">
              {isSearchingAll
                ? `Ni najdenih prevzemnih mest za iskalni niz "${query.trim()}".`
                : typeFilter === 2
                  ? `V vaši bližini ni paketomatov. Poskusite iskanje po kraju ali premaknite zemljevid.`
                  : typeFilter === 1
                    ? `V vaši bližini ni prevzemnih mest. Poskusite iskanje po kraju ali premaknite zemljevid.`
                    : `Za poštno številko ${postalCode} ni najdenih prevzemnih mest. Premaknite zemljevid ali poiščite po kraju.`}
            </Text>
          ) : (
            <GlsDeliveryPointList
              points={visiblePoints}
              selectedMatchcode={selectedPoint?.matchcode}
              focusedMatchcode={focusedMatchcode}
              saving={saving}
              showDistance={Boolean(homeCoordinate) && !isSearchingAll}
              onFocusPoint={focusPoint}
              onSelectPoint={selectPoint}
            />
          )}
        </>
      )}
    </div>
  )
}

export default GlsDeliveryPointPicker
